import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";

import React from "react";

export interface Column {
  id: string;
  label: string;
  width?: string | number;
  render?: (value: any, row: any) => React.ReactNode;
  align?: "left" | "right" | "center";
  isSorted: true | false;
}

interface SimpleTableProps {
  columns: Column[];
  rows: any[];
  rowsPerPage: number;
  page: number;
  handleChangePage?: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage?: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  isPagination?: boolean;
  paddingTop?: string;
  paddingBottom?: string;
  orderBy: string;
  orderDirection: string;
  onSort: any;
}

const SimpleTable = ({
  columns = [],
  rows = [],
  rowsPerPage = 10,
  page = 0,
  handleChangePage = () => {},
  handleChangeRowsPerPage = () => {},
  paddingTop = "14px",
  paddingBottom = "14px",
  isPagination = true,
  orderBy,
  orderDirection,
  onSort,
}: Partial<SimpleTableProps>) => {
  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ height: "calc(100vh - 30rem)", overflow: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => {
                const isSorted = orderBy === column.id;
                return (
                  <TableCell
                    key={column.id}
                    onClick={() => onSort && onSort(column.id)}
                    sx={{
                      cursor: onSort ? "pointer" : "default",
                      textAlign: column.align || "left",
                      width: column.width,
                      paddingTop,
                      paddingBottom,
                      paddingLeft: index === 0 ? 0 : "14px",
                      paddingRight: index === columns.length - 1 ? 0 : "14px",
                      userSelect: "none",
                    }}
                  >
                    {column.label}
                    {isSorted ? (orderDirection === "desc" ? "↑" : "↓") : ""}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length > 0 &&
              rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any, index: number) => (
                  <TableRow key={index}>
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          paddingTop: paddingTop,
                          paddingBottom: paddingBottom,
                          paddingLeft: colIndex === 0 ? 0 : "14px",
                          paddingRight:
                            colIndex === columns.length - 1 ? 0 : "14px",
                          borderBottom:
                            "1px solid rgba(0, 0, 0, 0.06) !important",
                          color: "#000000DE",
                          letterSpacing: "0.5px",
                          width: column.width,
                          textAlign: column.align || "left",
                        }}
                      >
                        {column.render
                          ? column.render(row[column.id], row)
                          : row[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isPagination && rows.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
          sx={{
            ".MuiTablePagination-selectLabel": {
              color: "#00000099",
              fontSize: "12px",
              letterSpacing: "0.4px",
            },
            ".MuiTablePagination-displayedRows": {
              fontSize: "12px",
              marginLeft: "26px",
              letterSpacing: "0.4px",
            },
            ".MuiTablePagination-select": {
              fontSize: "12px",
              margin: "0",
              letterSpacing: "0.4px",
            },
            ".MuiTablePagination-actions": {
              fontSize: "12px",
              letterSpacing: "0.4px",
            },
            ".MuiTablePagination-menuItem": {
              fontSize: "12px",
              letterSpacing: "0.4px",
            },
            ".MuiTablePagination-toolbar": {
              padding: "0 8px",
              minHeight: "40px",
            },
          }}
        />
      )}
    </Box>
  );
};

export default SimpleTable;
